import { useCallback, useEffect } from "react";

import { type Charge, chargesEndpoints } from "../../../api";
import { useStrapiList } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import { getFromCache, setToCache } from "../../../utils/memoryCache";
import handleError from "../../utils/handleError";

interface UseChargesProps {
  cacheKey?: string;
  mine?: boolean;
  autoLoad?: boolean;
  [key: string]: unknown;
}

const useCharges = (props?: UseChargesProps) => {
  const { cacheKey, mine } = props || {};
  const cached = cacheKey ? getFromCache<Charge[]>(cacheKey) : null;
  const isMine = !!mine;
  const load = useCallback((params: Record<string, unknown>) => loadCharges(params, isMine), [isMine]);
  const onLoadError = useCallback((e: unknown) => handleError(getErrorMessage(e, "Charges loading failed")), []);

  const { items: charges, isLoading, hasLoaded, ...other } = useStrapiList<Charge>({
    ...props,
    requestKey: JSON.stringify([cacheKey, isMine]),
    load,
    onLoadError,
  });
  useEffect(() => {
    // Only cache accepted responses for this query, including empty lists.
    if (cacheKey && hasLoaded && !isLoading && !other.error) setToCache(cacheKey, charges);
  }, [cacheKey, charges, hasLoaded, isLoading, other.error]);
  const hasCachedData = !hasLoaded && !!cached;

  return {
    charges: hasCachedData ? cached.data : charges,
    isLoading: isLoading && !hasCachedData,
    isRefreshing: isLoading && (hasLoaded || hasCachedData),
    ...other,
  };
};

export const loadCharges = (params: Record<string, unknown>, mine: boolean) =>
  mine ? chargesEndpoints.getMyCharges(params) : chargesEndpoints.getCharges(params);

export default useCharges;
